import { cn } from "@/common/helpers/utils";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FilterIcon } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export enum FilterActionKey {
  Clear = "clear",
  Reset = "reset",
}

type FilterAction = {
  key: FilterActionKey;
  label: string;
  onClick: () => void;
  disabled: boolean;
};

export interface FilterContainerProps {
  children: ReactNode;
  triggerLabel?: string;
  className?: string;
  triggerClassName?: string;
  showClearAll?: boolean;
  activeFilterCount?: number;
  hasDefaultFilters?: boolean;
  hasNonDefaultFilters?: boolean;
  onResetAll?: () => void;
  onClearAll?: () => void;
}

export function FilterContainer({
  children,
  className,
  triggerClassName,
  activeFilterCount = 0,
  hasDefaultFilters = false,
  hasNonDefaultFilters = false,
  onResetAll,
  onClearAll,
}: FilterContainerProps) {
  const { t } = useTranslation("common");
  const hasActiveFilters = activeFilterCount > 0;
  const resetAllLabel = t(hasDefaultFilters ? "RestoreDefaultFilters" : "ClearFilters");
  const clearAllAction = onClearAll
    ? ({
        key: FilterActionKey.Clear,
        label: t("ClearAllFilters"),
        onClick: onClearAll,
        disabled: !hasActiveFilters,
      } satisfies FilterAction)
    : undefined;
  const resetAllAction = onResetAll
    ? ({
        key: FilterActionKey.Reset,
        label: resetAllLabel,
        onClick: onResetAll,
        disabled: !hasNonDefaultFilters,
      } satisfies FilterAction)
    : undefined;
  const filterActions: FilterAction[] = [
    ...(clearAllAction ? [clearAllAction] : []),
    ...(resetAllAction ? [resetAllAction] : []),
  ];
  const hasFilterActions = filterActions.length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={hasActiveFilters ? ButtonVariant.Filled : ButtonVariant.Outlined}
          colorVariant={hasActiveFilters ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
          size={ButtonSize.Icon}
          className={cn("h-9 w-12 shrink-0", triggerClassName)}
        >
          <FilterIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("max-h-[83dvh] w-86 overflow-y-auto p-4", className)} align="start">
        <div className={hasFilterActions ? "space-y-4" : undefined}>
          {children}
          {hasFilterActions && (
            <div className="flex flex-col gap-2">
              {filterActions.map((action) => (
                <Button
                  key={action.key}
                  variant={ButtonVariant.Filled}
                  size={ButtonSize.Default}
                  onClick={action.onClick}
                  className="h-auto min-h-9 w-full justify-start px-3 text-left whitespace-normal"
                  disabled={action.disabled}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
